import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getNetworkEntities } from "../../../../Api/NetworkEntityApi";
import CODE from "../../../../Static/Constants/StatusCodes";
import { allowedEntityTypes } from "../workOrderHelpers";

// The options behind the New Work Order form's Source and Destination pickers.
//
// Fetched PER ENTITY TYPE, once, and only for the types the chosen movement
// types actually allow: a form with one dispatch movement should not pull every
// vendor, dealer and customer in the org for pickers it never renders. A type
// already requested is never requested again, so adding a second movement of
// the same kind costs nothing.
//
// Returned as {code: {records, isLoading, errorMessage}} rather than one flat
// list, because a FAILURE must not read as "this org has none configured":
// one is a retry, the other is a configuration task, and EntityPicker says so.

// The network entity list is paged and this form only PICKS from it. One page
// per entity type is fetched, which is the same ceiling the list endpoint
// enforces per request.
const ENTITY_PAGE_LIMIT = 100;

const useWorkOrderEntities = (movements, movementTypesByCode) => {
    const [entityState, setEntityState] = useState({});
    const requestedTypes = useRef(new Set());

    const neededEntityTypes = useMemo(() => {
        const needed = new Set();
        (movements || []).forEach((movement) => {
            const movementType = movementTypesByCode?.[movement?.movementTypeCode];
            if (!movementType) return;
            allowedEntityTypes(movementType, "source").forEach((c) => needed.add(c));
            allowedEntityTypes(movementType, "destination").forEach((c) => needed.add(c));
        });
        // Sorted and joined below rather than compared by reference: a new Set
        // every render would restart the effect on every keystroke.
        return [...needed].sort();
    }, [movements, movementTypesByCode]);

    // The dependency is the JOINED STRING for the same reason useWorkOrderConfig
    // keys on one: a fresh array literal each render is a fresh identity.
    const neededKey = neededEntityTypes.join(",");

    useEffect(() => {
        // Read once into a local. The Set behind this ref is created on mount
        // and never replaced, so capturing it is safe -- and the cleanup below
        // has to touch it, which the exhaustive-deps rule warns about when the
        // cleanup reaches through `.current` itself.
        const requested = requestedTypes.current;
        const toFetch = (neededKey ? neededKey.split(",") : []).filter(
            (code) => !requested.has(code)
        );
        if (!toFetch.length) return undefined;

        let cancelled = false;
        toFetch.forEach((code) => requested.add(code));
        setEntityState((prev) => {
            const next = { ...prev };
            toFetch.forEach((code) => {
                next[code] = { records: [], isLoading: true, errorMessage: "" };
            });
            return next;
        });

        (async () => {
            await Promise.all(
                toFetch.map(async (code) => {
                    const res = await getNetworkEntities({
                        entityTypeCode: code,
                        page: 1,
                        limit: ENTITY_PAGE_LIMIT,
                    });
                    if (cancelled) return;
                    const ok = res?.responseStatus?.code === CODE.SUCCESS;
                    setEntityState((prev) => ({
                        ...prev,
                        [code]: {
                            records: ok ? res?.data?.records || [] : [],
                            isLoading: false,
                            errorMessage: ok
                                ? ""
                                : res?.responseStatus?.msg ||
                                  `Could not load ${code} entities`,
                        },
                    }));
                    // A failed type is allowed to be asked for again the next
                    // time it is needed.
                    if (!ok) requested.delete(code);
                })
            );
        })();

        return () => {
            cancelled = true;
            // AND released, not just ignored. A cleanup that only sets the flag
            // leaves these codes marked as requested, so the re-run skips them
            // and their pickers spin on isLoading for the rest of the session.
            // The failure path above releases them for exactly the same reason.
            toFetch.forEach((code) => requested.delete(code));
        };
    }, [neededKey]);

    // A partner created from inside the form, folded into the list it will be
    // picked from.
    //
    // WITHOUT THIS the user adds a customer, the dialog closes, and the picker
    // still does not contain them -- because this hook fetches each entity
    // type once and never again. The alternative was re-fetching the whole
    // type, which is a request for a record we are already holding.
    //
    // Prepended, so the thing just created is the first thing offered.
    // Replaces any row with the same id rather than appending a second copy.
    const addEntity = useCallback((entityTypeCode, record) => {
        if (!entityTypeCode || record?.id === null || record?.id === undefined) return;
        setEntityState((prev) => {
            const bucket = prev[entityTypeCode] || {
                records: [],
                isLoading: false,
                errorMessage: "",
            };
            return {
                ...prev,
                [entityTypeCode]: {
                    ...bucket,
                    records: [
                        record,
                        ...(bucket.records || []).filter(
                            (existing) => String(existing?.id) !== String(record.id)
                        ),
                    ],
                },
            };
        });
    }, []);

    // An object rather than the bare state: the form needs to WRITE into this
    // list as well as read it, now that a customer can be created from the
    // destination picker.
    return { entityState, addEntity };
};

export default useWorkOrderEntities;
